import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { useForm } from 'react-hook-form';

import { calendarApi } from '../../features/calendar';
import createStore from './store';

import { IInputFormFactoryForm, InputFormFactory, InputFormFactoryType } from '../../components/input-form';
import { Fieldset } from '../../components/fieldset';
import { FieldsetFactoryType, FieldsetFormFactory } from '../../components/fieldset-form';

import { RegexpUtility } from '../../utilities/regexp';
import { DateUtility } from '../../utilities/date';
import { ObjectUtility } from '../../utilities/object';

import { TimeZoneOptions } from '../../models/timezone';
import {
    IAddCalendarEventFormValue,
    ICalendarEvent,
    IContactInformation,
    IEventInformation,
    IEventLocation
} from '../../models/event';
import { Spinner } from '../../components/spinner';
import { ApiUtility } from '../../utilities/api';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import { Alert, AlertType } from '../../components/alert';
import { ErrorBoundary } from '../../components/error-boundary';
import { FormUtility } from '../../utilities/form';
import { Button, ButtonType } from '../../components/button';
declare const Resources: any;

interface IAddCalendarEventAppProps {
    index: number;
    calendarRootId: number;
}

const currentTimeZone = DateUtility.CurrentTimeZone + '';
const SHORT_TITLE_MAX_LENGTH_RULE = {
    value: 30,
    message: 'Input length cannot be longer than 30 characters'
};
const MAX_LENGTH_RULE = {
    value: 255,
    message: 'Input length cannot be longer than 255 characters'
};
const REQUIRED = {
    validate: FormUtility.Required
};

const AddCalendarEventDetailForm = ({ index, calendarRootId }: IAddCalendarEventAppProps) => {
    const { getValues, control, handleSubmit, formState } = useForm({
        mode: 'onBlur'
    });
    const [addEvent, { isSuccess, isError, error }] = calendarApi.useSaveEventMutation();
    const {
        data: categories,
        isLoading: isLoadingCategory,
        error: categoryError,
        isError: isCategoryError
    } = calendarApi.useGetCategoriesQuery(calendarRootId);

    const categoryErrorMessage = React.useMemo(() => {
        return ApiUtility.getFetchErrorMessage(categoryError as FetchBaseQueryError);
    }, [categoryError]);

    const onSubmit = async (newEventData: IAddCalendarEventFormValue) => {
        const contactInformation: IContactInformation = ObjectUtility.extractDataFromObject(newEventData, [
            'fullName',
            'emailAddress',
            'phoneNumber',
            'organization',
            'noteToCalendarAdministrator'
        ]);
        const eventInformation: Omit<IEventInformation, 'categories'> = ObjectUtility.extractDataFromObject(
            newEventData,
            ['eventTitle', 'shortEventTitle', 'startDate', 'startTime', 'endDate', 'endTime', 'description', 'webPageLink'],
            {
                timezoneInput: (data: IAddCalendarEventFormValue) =>
                    data.timezoneInput ? data.timezoneInput : currentTimeZone,
                categoriesInput: (data: IAddCalendarEventFormValue) =>
                    data.categories?.map(option => Number(option.value)) || []
            }
        );
        const eventLocation: IEventLocation = ObjectUtility.extractDataFromObject(newEventData, [
            'locationName',
            'street',
            'city',
            'stateProvince',
            'zipPostalCode',
            'country'
        ]);
        const newEvent: ICalendarEvent = {
            contactInformation,
            eventInformation,
            eventLocation
        };
        await addEvent({
            id: calendarRootId,
            newEvent
        });
    };
    const formName = `add-calendar-event-${index}`;
    const form: IInputFormFactoryForm = {
        name: formName,
        control
    };

    const submitErrorMessage = React.useMemo(() => {
        return ApiUtility.getFetchErrorMessage(error as FetchBaseQueryError);
    }, [error]);

    if (isLoadingCategory) {
        return (
            <div className='form-state-block d-flex align-items-center justify-content-center'>
                <Spinner />
            </div>
        );
    } else if (isCategoryError) {
        return (
            <Alert type={AlertType.Error}>
                <div>Something went wrong while getting the list of categories!</div>
                {categoryErrorMessage}
            </Alert>
        );
    }

    if (isSuccess) {
        return <Alert type={AlertType.Success}>{Resources.successMessage}</Alert>;
    }

    return (
        <form onSubmit={handleSubmit(onSubmit)} className='add-calendar-event-form'>
            {formState?.isSubmitting ? <Spinner isOverlay={true} /> : ''}
            {isError ? (
                <Alert type={AlertType.Error}>
                    <div>Something went wrong!</div>
                    {submitErrorMessage}
                </Alert>
            ) : (
                ''
            )}


            {/* Contact Information */}
            <Fieldset legend='Contact Information' disabled={formState?.isSubmitting}>
                {/* Full Name - required */}
                <InputFormFactory
                    label='Full name'
                    name='fullName'
                    required={true}
                    form={form}
                    validations={{ maxLength: MAX_LENGTH_RULE, ...REQUIRED }}
                />
                {/* Email Address - required - email */}
                <InputFormFactory
                    label='Email'
                    name='emailAddress'
                    type={InputFormFactoryType.Email}
                    required={true}
                    form={form}
                    validations={{ maxLength: MAX_LENGTH_RULE, ...REQUIRED }}
                />
                {/* Phone Number */}
                <InputFormFactory
                    label='Phone number'
                    name='phoneNumber'
                    form={form}
                    validations={{
                        pattern: {
                            value: RegexpUtility.PhoneNumber,
                            message: `Phone number should only consists of number (plus sign at the beginning is optional)`
                        },
                        maxLength: MAX_LENGTH_RULE
                    }}
                />
                {/* Organization */}
                <InputFormFactory
                    label='Organization'
                    name='organization'
                    form={form}
                    validations={{ maxLength: MAX_LENGTH_RULE }}
                />
                {/* Note to Calendar Admin - Text Area */}
                <InputFormFactory
                    label='Note to Calendar Administrator'
                    name='noteToCalendarAdministrator'
                    type={InputFormFactoryType.TextArea}
                    form={form}
                />
            </Fieldset>

            {/* Event detail */}
            <Fieldset legend='Event detail' disabled={formState?.isSubmitting}>
                {/* Event Title - required */}
                <InputFormFactory
                    label='Event title'
                    name='eventTitle'
                    required={true}
                    form={form}
                    validations={{ maxLength: MAX_LENGTH_RULE, ...REQUIRED }}
                />

                {/* Short Event Title - required */}
                <InputFormFactory
                    label='Short Event Title'
                    name='shortEventTitle'
                    required={true}
                    form={form}
                    validations={{ maxLength: SHORT_TITLE_MAX_LENGTH_RULE, ...REQUIRED }}
                />

                {/* Event Category */}
                <FieldsetFormFactory
                    label='Event category'
                    name='categories'
                    type={FieldsetFactoryType.Select}
                    selectProps={{
                        options: categories?.map(category => ({
                            value: category.categoryId + '',
                            label: category.categoryName
                        })),
                        isMulti: true
                    }}
                    form={form}
                    disabled={formState.isSubmitting}
                />

                {/* Timezone */}
                <FieldsetFormFactory
                    label='Timezone'
                    name='timezoneInput'
                    type={FieldsetFactoryType.Select}
                    required={true}
                    selectProps={{
                        options: TimeZoneOptions,
                        defaultValue: TimeZoneOptions.filter(option => option.value === currentTimeZone)[0] || null
                    }}
                    form={form}
                    disabled={formState.isSubmitting}
                />

                {/* Start Date - required */}
                <FieldsetFormFactory
                    label='Start date'
                    name='startDate'
                    type={FieldsetFactoryType.DatePicker}
                    required={true}
                    form={form}
                    validations={{
                        validate: {
                            validDate: (value: string) =>
                                DateUtility.isValidDateStringByFormat(value) || 'Start date is not a valid date'
                        }
                    }}
                    disabled={formState.isSubmitting}
                />

                {/* Start Time */}
                <FieldsetFormFactory
                    label='Start time'
                    name='startTime'
                    type={FieldsetFactoryType.TimePicker}
                    required={true}
                    form={form}
                    disabled={formState.isSubmitting}
                />

                {/* End Date - required */}
                <FieldsetFormFactory
                    label='End date'
                    name='endDate'
                    type={FieldsetFactoryType.DatePicker}
                    required={true}
                    form={form}
                    validations={{
                        validate: {
                            validDate: (endDate: string) =>
                                DateUtility.isValidDateStringByFormat(endDate) || 'End date is not a valid date',
                            earlierThanStartDate: (endDate: string) => {
                                const startDate = getValues('startDate');
                                return (
                                    DateUtility.compareByFormat(endDate, startDate) >= 0 ||
                                    'End date must not be an earlier date than start date'
                                );
                            }
                        }
                    }}
                    disabled={formState.isSubmitting}
                />

                {/* End Time */}
                <FieldsetFormFactory
                    label='End time'
                    name='endTime'
                    type={FieldsetFactoryType.TimePicker}
                    required={true}
                    form={form}
                    validations={{
                        validate: {
                            earlierThanStartTime: (endTime: string) => {
                                const startDate = getValues('startDate');
                                const endDate = getValues('endDate');
                                const startTime = getValues('startTime');
                                if (DateUtility.compareByFormat(startDate, endDate) === 0 && startTime && endTime) {
                                    return endTime > startTime || 'End time must be later than start time';
                                }
                                return true;
                            }
                        }
                    }}
                    disabled={formState?.isSubmitting}
                />

                {/* Description */}
                <InputFormFactory label='Description' name='description' type={InputFormFactoryType.TextArea} required={true} form={form} validations={{ maxLength: MAX_LENGTH_RULE, ...REQUIRED }} />

                {/* Web Page Link */}
                <InputFormFactory
                    label='Web page link'
                    name='webPageLink'
                    form={form}
                    validations={{
                        pattern: {
                            value: RegexpUtility.WebUrl,
                            message: `Web page link is not a valid web page address`
                        },
                        maxLength: MAX_LENGTH_RULE
                    }}
                />
            </Fieldset>

            {/* Location detail */}
            <Fieldset legend='Location detail' disabled={formState.isSubmitting}>
                {/* Location Name */}
                <InputFormFactory
                    label='Location Name'
                    name='locationName'
                    required={true}
                    form={form}
                    validations={{ maxLength: MAX_LENGTH_RULE }}
                />
                {/* Street */}
                <InputFormFactory label='Street' name='street' form={form} validations={{ maxLength: MAX_LENGTH_RULE }} />
                {/* City */}
                <InputFormFactory label='City' name='city' form={form} validations={{ maxLength: MAX_LENGTH_RULE }} />
                {/* State/Province */}
                <InputFormFactory
                    label='State/Province'
                    name='stateProvince'
                    form={form}
                    validations={{ maxLength: MAX_LENGTH_RULE }}
                />
                {/* Zip/Postal Code */}
                <InputFormFactory
                    label='Zip/Postal Code'
                    name='zipPostalCode'
                    form={form}
                    validations={{ maxLength: MAX_LENGTH_RULE }}
                />
                {/* Country */}
                <InputFormFactory label='Country' name='country' required={true} form={form} validations={{ maxLength: MAX_LENGTH_RULE }} />
            </Fieldset>

            <div className='d-flex justify-content-center'>
                <Button type={ButtonType.Submit} disabled={formState?.isSubmitting}>
                    Create event
                </Button>
            </div>
        </form>
    );
};

enum AddCalendarEventRenderType {
    Error = 'error',
    Loading = 'loading',
    Form = 'form', // showing full form
    Ineligible = 'ineligible', // logged in but not enough privilege
    NotLoggedIn = 'not logged in', // not logged in as a member
    Backoffice = 'backoffice' // admin or privileged user
}

export const AddCalendarEventApp = ({ index, calendarRootId }: IAddCalendarEventAppProps) => {
    const { data: permission, isLoading, isError, error } = calendarApi.useGetPermissionOnCalendarQuery(calendarRootId);
    const getPermissionErrorMessage = React.useMemo(() => {
        return ApiUtility.getFetchErrorMessage(error as FetchBaseQueryError);
    }, [error]);

    const renderType = React.useMemo(() => {
        if (isLoading) {
            return AddCalendarEventRenderType.Loading;
        }
        if (isError || !permission) {
            return AddCalendarEventRenderType.Error;
        }
        const { isUserLoggedIn, userHasPrivilege, isMemberLoggedIn, memberHasPrivilege, isAdmin } = permission;
        if (isMemberLoggedIn && memberHasPrivilege) {
            return AddCalendarEventRenderType.Form;
        }
        if ((isMemberLoggedIn && !memberHasPrivilege) || (isUserLoggedIn && !userHasPrivilege && !isAdmin)) {
            return AddCalendarEventRenderType.Ineligible;
        }
        if (!isMemberLoggedIn && !isUserLoggedIn) {
            return AddCalendarEventRenderType.NotLoggedIn;
        }
        if (isUserLoggedIn && (userHasPrivilege || isAdmin)) {
            return AddCalendarEventRenderType.Backoffice;
        }
    }, [permission]);

    const renderForm = React.useCallback(() => {
        if (!renderType || renderType === AddCalendarEventRenderType.Loading) {
            return (
                <div className='form-state-block d-flex align-items-center justify-content-center'>
                    <Spinner />
                </div>
            );
        }
        switch (renderType) {
            case AddCalendarEventRenderType.Error:
                return (
                    <Alert type={AlertType.Error}>
                        <div>Something went wrong!</div>
                        {getPermissionErrorMessage}
                    </Alert>
                );
            case AddCalendarEventRenderType.Ineligible:
                return <Alert type={AlertType.Error}>You do not have permission to create calendar event</Alert>;
            case AddCalendarEventRenderType.NotLoggedIn:
                return <Alert type={AlertType.Warning}>You need to log in in order to create calendar event</Alert>;
            case AddCalendarEventRenderType.Backoffice:
                return (
                    <Alert type={AlertType.Warning}>
                        Since you are a backoffice user, you should go to the CMS to create an event
                    </Alert>
                );
            case AddCalendarEventRenderType.Form:
                return <AddCalendarEventDetailForm {...{ index, calendarRootId }} />;
            default:
                return null;
        }
    }, [renderType]);

    return <>{renderForm()}</>;
};

export class AddCalendarEventForm {
    constructor(element: HTMLElement, index: number) {
        const store = createStore();
        const calendarRootId = Number(element.dataset.calendarRootId);
        if (!calendarRootId || isNaN(calendarRootId)) {
            console.log(`invalid calendar root node id: ${calendarRootId}`);
            return;
        }
        ReactDOM.render(
            <Provider store={store}>
                <ErrorBoundary>
                    <AddCalendarEventApp {...{ index, calendarRootId }} />
                </ErrorBoundary>
            </Provider>,
            element
        );
    }
}
